import React, { useEffect, useState } from "react";
import { Box, styled, Typography, Button, Grid2, Divider } from "@mui/material";
import { useTranslation } from "react-i18next";
import { getColumnsConfig } from "../../../../Features/Table/GenericDraggableTable/GenericDraggableTableFuncations";
import { usePagesContent } from "../../../../Hooks/usePagesContent";
import AppCache from "../../../../Helper/Common/AppCache";
import { fetchFiltersDataObject } from "../../../../Helper/Table/TableHelper";
import {
  convertAndGetTimeStampInRequiredFormat,
  convertZonetoUTCEnd,
  convertZonetoUTCStart,
  getDatePickerDate,
  analyticsCalendarEndDate,
} from "../../../../Helper/DatePicker/DateConverter";
import { useBatteryInfo, useVehicleInfo } from "../../../../Pages/DigitalTwinV3/DigitalTwinContext";
import { decryptTheParams, encryptTheParams } from "../../../../Helper/QueryParams/EncryptDecrypt";
import GenericDraggableTableView from "../../../../Features/Table/GenericDraggableTable";
import DeviceIDLink from "../../../../Features/Table/GenericDraggableTable/Renderers/columnTypes/DeviceIDLink";
import { MAX_COLUMN_SELECTION_COUNT } from "../../../../Helper/DigitalTwin/DataFile";
import {
  alertListAction,
  getBatteryAlertIncidents,
} from "../../../../Api/BmsFaultsApi";
import AlertActionDialog from "../../../BmsFaults/AlertActionDialog";
import { PrimaryTooltip } from "../../../../Components/StyledComponent";
import { usePermissions } from "../../../../Context/PermissionContext";
import { FEATURE_CODES } from "../../../../Static/FeatureMapping";
import CustomIcon from "../../../../Components/CustomIcon";
import SingleAlertAnalysisDialog from "../../../BmsFaults/SingleAlertAnalysis/SingleAlertAnalysisDialog";
import dayjs from "dayjs";
import { isOrganizationJBM } from "../../../../Helper/Organization/Functions";
import { useNavigate } from "react-router-dom";
import ChartCalendar from "../../ChartCalendar";
import BatteryTriggeredFaultChart from "./BatteryTriggeredFaultChart";
import BatteryResolutionDurationChart from "./BatteryResolutionDurationChart";
import BmsFaultDrillDownModal from "./BmsFaultDrillDownModal";

const TableContainer = styled(Box)(({ theme }) => ({
  minHeight: "200px",
}));

const StyledAction = styled(Button)(() => ({
  padding: '4px 12px',
  backgroundColor: '#FFFFFF',
  border: '1px solid #E6E6E6',
  borderRadius: '8px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  cursor: 'pointer',
  textTransform: 'none',
  minWidth: 0,

  '&:hover': {
    border: '1px solid #6D72F6',

    '& span': {
      color: '#6D72F6'
    },

    '& svg path': {
      fill: '#6D72F6'
    }
  }
}));

const TABLE_ID = "BatteryBmsFaults";

const defaultStartDate = convertZonetoUTCStart(
  getDatePickerDate(undefined, -90)
);
const defaultEndDate = convertZonetoUTCEnd(getDatePickerDate(undefined, 0));

const defaultChartStartDate = dayjs().subtract(1, 'month').subtract(1, 'day').format('YYYY-MM-DD');
const defaultChartEndDate = analyticsCalendarEndDate();

const getDefaultFilters = (queryParams) => {
  try {
    let queryFilters = queryParams?.bmsFaultFilters || "[]";
    queryFilters = JSON.parse(queryFilters);
    if (queryFilters && Object.keys(queryFilters).length > 0) {
      return queryFilters;
    }
  } catch (error) {
    // ignore malformed query param
  }
  return {};
};

const BatteryBmsFaults = ({ batteryID }) => {
  const navigate = useNavigate();
  const { t } = useTranslation(["common", "generic_table"]);
  const { batteryInfo } = useBatteryInfo();
  const vehicleInfo = useVehicleInfo();
  const pageContent = usePagesContent();
  const { hasPermission, isRbacV2Enabled } = usePermissions();
  const canUpdate = !isRbacV2Enabled() || hasPermission(FEATURE_CODES.BMS_FAULTS_V2, 'update');
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [defaultSelectedColumns, setDefaultSelectedColumns] = useState([]);
  const [columnOrder, setColumnOrder] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [availableColumns, setAvailableColumns] = useState([]);
  const [currentTableColumns, setCurrentTableColumns] = useState([]);
  const [availableFilters, setAvailableFilters] = useState({});
  const [selectedFilters, setSelectedFilters] = useState(getDefaultFilters(decryptTheParams()));
  const [selectedRowKeys, setSelectedRowKeys] = useState({});
  const [tableData, setTableData] = useState([]);
  const [pageInfo, setPageInfo] = useState({});
  const [unfilteredTotal, setUnfilteredTotal] = useState(null);
  const [sorting, setSorting] = useState([
    { sortBy: "bmsAlertStatus", order: "asc" },
    { sortBy: "severityLevelID", order: "desc" },
    { sortBy: "customerDBTimeInsert", order: "desc" },
  ]);
  const [isSelectAll, setIsSelectAll] = useState(false);
  const [totalSelectedRowKeys, setTotalSelectedRowKeys] = useState(new Set());
  const [openAnalyseDialog, setOpenAnalyseDialog] = useState(false);
  const [analyseDialogData, setAnalyseDialogData] = useState({});
  const [openActionDialog, setOpenActionDialog] = useState(false);
  const [actionDialogData, setActionDialogData] = useState({});
  const [chartStartDate, setChartStartDate] = useState(defaultChartStartDate);
  const [chartEndDate, setChartEndDate] = useState(defaultChartEndDate);
  const [drilldownOpen, setDrilldownOpen] = useState(false);
  const [drilldownChartName, setDrilldownChartName] = useState(null);
  const [drilldownFaultName, setDrilldownFaultName] = useState(null);
  const [drilldownResolutionType, setDrilldownResolutionType] = useState(null);
  const [drilldownSeverityName, setDrilldownSeverityName] = useState(null);

  const handleChartBarClick = (chartName, faultName, resolutionType = null, severityName = null) => {
    setDrilldownChartName(chartName);
    setDrilldownFaultName(faultName);
    setDrilldownResolutionType(resolutionType);
    setDrilldownSeverityName(severityName);
    setDrilldownOpen(true);
  };

  const pageCount = 20;

  const handleAnalyseClick = (alertIncident) => {
    // Remove "LV" prefix if it exists
    const severityWithoutLV = alertIncident.severityLevel?.replace(/^LV/, '') || alertIncident.severityLevel;
    const modifiedAlertIncident = {
      ...alertIncident,
      severityLevel: severityWithoutLV
    }
    setAnalyseDialogData({
      battery: {
        ...batteryInfo,
        ...vehicleInfo,
      },
      alertIncident: modifiedAlertIncident
    });
    setOpenAnalyseDialog(true);
  }

  const handleAdvancedAnalyticsClick = (alertIncident) => {
          // Prepare events array with the incident error code
          const events = alertIncident?.incidentErrorName ? [alertIncident.incidentErrorName] : [];

          // Start date: incident trigger date
          const startDate = alertIncident?.customerDBTimeInsert
              ? convertAndGetTimeStampInRequiredFormat(alertIncident?.customerDBTimeInsert, "YYYY-MM-DD")
              : null;

          // End date: resolution date if resolved, otherwise today
          let endDate;
          if (alertIncident?.resolveCustomerDBTime) {
              endDate = convertAndGetTimeStampInRequiredFormat(alertIncident?.resolveCustomerDBTime, "YYYY-MM-DD");
          } else {
              endDate = dayjs().format("YYYY-MM-DD");
          }

          let temp = {
              batteryID: batteryInfo?.batteryID,
              deviceID: batteryInfo?.deviceID,
              selectedType: 'deviceID',
              selectedTypeId: batteryInfo?.deviceID,
              events: JSON.stringify(events),
              startDate: startDate,
              endDate: endDate,
              entityAffectedType: alertIncident?.entityAffected || 'battery',
              entityAffectedId: alertIncident?.entityAffectedID,
              category: alertIncident?.category || null,
          };
          if (isOrganizationJBM() && batteryInfo?.licensePlateNo) {
              temp = {
                  ...temp,
                  selectedType: 'vehicle',
                  selectedTypeId: batteryInfo?.licensePlateNo
              }
          }
          encryptTheParams(temp, navigate, false, '/advanced-analysis', true);
      };

  const handleActionClick = (alertIncident) => {
    // Remove "LV" prefix if it exists
    const severityWithoutLV = alertIncident.severityLevel?.replace(/^LV/, '') || alertIncident.severityLevel;
    const modifiedAlertIncident = {
      ...alertIncident,
      severityLevel: severityWithoutLV
    }
    setActionDialogData({
      battery: {
        ...batteryInfo,
        ...vehicleInfo,
      },
      alertIncident: modifiedAlertIncident
    });
    setOpenActionDialog(true);
  };

  const customRenderers = {
    'device-id-link': (record, config) => {
      const handleBusNumberClick = () => {
        const batteryId = record.xLabel;
        if (!batteryId) return;
        const hash = encryptTheParams({ batteryID: batteryId });
        window.open(`/specificBattery?${hash}`, "_blank", "noreferrer");
      };
      return <DeviceIDLink record={record} config={config} onTextClick={handleBusNumberClick} />;
    },
    bms_fault_action: (record, config) => {
      let actionCtaLabel = 'View Details';
      if (canUpdate) {
        const bmsAlertStatusKey = record.bmsAlertStatus?.toLowerCase();
        if (bmsAlertStatusKey === 'open') {
          actionCtaLabel = 'Act Now';
        } else if (!['resolved', 'force resolved', 'auto resolved'].includes(bmsAlertStatusKey)) {
          actionCtaLabel = 'Update';
        }
      }

      return (
        <Grid2 container alignItems={'center'} gap={1.5}>
          <PrimaryTooltip
            placement="bottom"
            title="Analyse"
          >
            <StyledAction onClick={() => handleAnalyseClick(record)} sx={{ py: '7px', px: 1 }}>
              <CustomIcon name="binocular-icon" fill='#494949' />
            </StyledAction>
          </PrimaryTooltip>
          <Divider orientation="vertical" flexItem sx={{ borderColor: "#E4E4E4", my: 0.75 }} />
          <PrimaryTooltip
            placement="bottom"
            title="Advanced Analysis"
          >
            <StyledAction onClick={() => handleAdvancedAnalyticsClick(record)} sx={{ py: '7px', px: 1 }}>
              <CustomIcon name='advanced-analysis-settings' fill="#494949" />
            </StyledAction>
          </PrimaryTooltip>
          <Divider orientation="vertical" flexItem sx={{ borderColor: "#E4E4E4", my: 0.75 }} />
          <StyledAction onClick={() => handleActionClick(record)}>
            <Typography variant="fw500s14c494949" sx={{ lineHeight: '20px' }}>
              {actionCtaLabel}
            </Typography>
          </StyledAction>
        </Grid2>
      );
    },
  };

  const formatIncidentData = (data) => {
    return data.map((incident) => ({
      ...incident,
      id: incident.incidentID,
      severityLevel: incident.severityLevel
        ? `LV${incident.severityLevel}`
        : "",
    }));
  };

  const fetchDigitalTwinAlerts = async () => {
    try {
      setIsLoading(true);
      const defaultFilters = {
        batteryID: [batteryID],
      };

      const activatedAtFilter = selectedFilters.activatedAt
        ? JSON.parse(selectedFilters.activatedAt)
        : {};

      const startDate = activatedAtFilter.startDateUTC || defaultStartDate;
      const endDate = activatedAtFilter.endDateUTC || defaultEndDate;

      const filters = {
        ...selectedFilters,
        ...defaultFilters,
        pageCount: pageCount,
        pageNum: pageNumber,
        sortBy: JSON.stringify(sorting),
      };
      const responseData = await getBatteryAlertIncidents(
        {},
        batteryID,
        filters
      );

      const data = responseData.response.data || [];
      const pageInfo = responseData.response.total || {};
      setPageInfo(pageInfo);
      const hasActiveFilters = selectedFilters && Object.keys(selectedFilters).length > 0;
      if (!hasActiveFilters) {
        setUnfilteredTotal(pageInfo.total ?? null);
      }
      setTableData(formatIncidentData(data));
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  };

  const applySelectedColumns = (newSelectedColumns) => {
    AppCache.setItem(TABLE_ID, { selectedColumns: newSelectedColumns });
    setSelectedColumns(newSelectedColumns);
  };

  const applySelectedFilters = (filters) => {
    return setSelectedFilters(filters);
  };

  const onPageChange = (pageNo) => {
    setPageNumber(pageNo);
  };

  const getTotalRowSelection = () => {
    return Array.from(totalSelectedRowKeys);
  };

  const getTotalRowSelectionCount = () => {
    if (isSelectAll) {
      return pageInfo?.total;
    }
    return totalSelectedRowKeys.size;
  };

  const getCurrentPageSelection = () => {
    return Array.from(totalSelectedRowKeys);
  };

  const onSelectAllChange = (selected, selectedRows, changeRows) => {
    setIsSelectAll(selected);
    if (selected) {
      setTotalSelectedRowKeys(new Set(selectedRows.map((row) => row?.id)));
      // setTotalSelectedRowBatteryIds(new Set(selectedRows.map((row) => row?.batteryID)))
      // setTotalSelectedRowVehicleNumbers(new Set(selectedRows.map((row) => row?.licensePlateNo)))
      // setTotalRowKeysForSelectAll()
      return;
    }

    setTotalSelectedRowKeys(new Set());
    // setTotalSelectedRowBatteryIds(new Set())
    // setTotalSelectedRowVehicleNumbers(new Set())
    return;
  };

  const onSelectChange = (record, selected, selectedKeys) => {
    const updatedSelectedKeys = new Set(totalSelectedRowKeys);
    if (selected) {
      updatedSelectedKeys.add(record.id);
    } else {
      updatedSelectedKeys.delete(record.id);
      if (isSelectAll) {
        setIsSelectAll(false);
      }
    }

    setTotalSelectedRowKeys(updatedSelectedKeys);
  };

  const applySort = (key, order) => {
    const sortDetails = { sortBy: key, order: order };
    setSorting(sortDetails);
  };

  const formatColumnConfigsForCSV = (coulmnConfigs) => {
    return coulmnConfigs.map((cConfig) => {
      if (cConfig.dataIndex.toLowerCase() == "resolutiontime") {
        return { ...cConfig, headerLabel: "Resolution Time (Minutes)" };
      }

      return { ...cConfig };
    });
  };

  const getCsvColumnParams = () => {
    const csvColumnConfigs = formatColumnConfigsForCSV(currentTableColumns);
    return {
      columnOrder: csvColumnConfigs.map((column) => column.headerLabel),
      columns: JSON.stringify(csvColumnConfigs),
    };
  };

  const getCSVFileName = () => {
    return `BatteryBMSFaults_${batteryInfo.deviceID
      }_${new Date().toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })}.csv`;
  };

  const prepareActionData = () => {
    const { activatedAt: selectedActivatedAt, ...restFilters } = selectedFilters;
    const filters = {
      ...restFilters,
      ...(selectedActivatedAt ? { activatedAt: Array.isArray(selectedActivatedAt) ? selectedActivatedAt[0] : selectedActivatedAt } : {}),
      batteryID: [batteryID],
      errorStatus: ["resolved", "active"],
    };
    const { columns, columnOrder: csvColumnOrder } = getCsvColumnParams();
    return {
      resourceIDs: isSelectAll ? [] : getTotalRowSelection(),
      filters: JSON.stringify(filters),
      columns,
      columnOrder: csvColumnOrder,
    };
  };

  const getCSVData = async () => {
    const actionData = prepareActionData();
    const res = await alertListAction({}, {
      ...actionData,
      actionType: "download-csv",
    });
    const csvData = res?.response?.data || [];
    return csvData;
  };

  const onSendEmail = async ({ sendTo, sendCc, subject, body }) => {
    const actionData = prepareActionData();
    await alertListAction({}, {
      ...actionData,
      emailTo: sendTo,
      emailCc: sendCc,
      subject: subject,
      body: body,
      actionType: "mail",
    });

    return [];
  };

  const setupTableColumns = () => {
    const tableInfo = pageContent?.metaInfo?.table?.bmsFaultsTable || {};
    const tableColumns = tableInfo?.columnsConfig || {};
    const tableColumnOrder = tableInfo?.columnOrdering || [];
    const availableColumns = getColumnsConfig({
      tableColumns: tableColumns,
      tableColumnOrder: tableColumnOrder,
      customRenderers,
      t,
    });

    setAvailableColumns(availableColumns);

    // const initalSelectedColumns = AppCache.getItem(TABLE_ID)?.selectedColumns || Object.keys(tableColumns) || []
    const initalSelectedColumns = Object.keys(tableColumns) || [];

    // sort by fixed key and then pick the first MAX_COLUMN_SELECTION_COUNT columns
    const defaultColumns = initalSelectedColumns
      .sort((a, b) => {
        if (a.fixed && !b.fixed) return -1;
        if (!a.fixed && b.fixed) return 1;
        return 0;
      })
      .slice(0, MAX_COLUMN_SELECTION_COUNT);

    setSelectedColumns(defaultColumns);
    setDefaultSelectedColumns(defaultColumns);

    const currentTableColumns = availableColumns.filter((column) =>
      initalSelectedColumns.includes(column.key)
    );
    setCurrentTableColumns(currentTableColumns);
    setColumnOrder(currentTableColumns.map((column) => column.headerLabel));
  };

  const setupTableFilters = async () => {
    const tableFilters =
      pageContent?.metaInfo?.filterConfigurations?.bmsFaultsTable?.filters ||
      [];
    const filtersObj = await fetchFiltersDataObject(
      tableFilters,
      availableFilters
    );

    if (filtersObj?.hierarchy?.values && !filtersObj.hierarchy.values.some(v => v.id === 'tms')) {
      filtersObj.hierarchy.values.push({ id: 'tms', value: 'TMS' });
    }

    const sortedFiltersObj = Object.fromEntries(
      Object.entries(filtersObj).sort(([, a], [, b]) =>
        a.label.localeCompare(b.label)
      )
    );
    setAvailableFilters(sortedFiltersObj);
  };

  const onColumnOrderChange = (tableColumns) => {
    const updatedColumnOrder = tableColumns.map((value) => value.headerLabel);
    setColumnOrder(updatedColumnOrder);
  };

  const getCountText = () => {
    const displayTotal = unfilteredTotal !== null ? unfilteredTotal : pageInfo.total;
    if (!displayTotal) return "";
    return displayTotal;
  };

  const getFilteredCount = () => {
    if (!selectedFilters || Object.keys(selectedFilters).length < 1) {
      return false;
    }
    return pageInfo.total ? pageInfo.total : 0;
  };

  const getNoDataMessage = () => {
    if (!selectedFilters || Object.keys(selectedFilters) < 1) {
      return "No issues found in this battery";
    }

    return "No matches found for filters applied";
  };

  useEffect(() => {
    setupTableColumns();
    setupTableFilters();
  }, [pageContent]);

  useEffect(() => {
    const currentTableColumns = availableColumns.filter((column) =>
      selectedColumns.includes(column.key)
    );
    setCurrentTableColumns(currentTableColumns);
  }, [selectedColumns]);

  useEffect(() => {
    pageNumber === 1 ? fetchDigitalTwinAlerts() : onPageChange(1);
  }, [selectedFilters, sorting]);

  useEffect(() => {
    fetchDigitalTwinAlerts();
  }, [pageNumber]);

  const bmsFaultsMeta = pageContent?.metaInfo?.charts?.bmsFaults;
  const hasTriggeredChart = !!bmsFaultsMeta?.triggeredFaultsChart;
  const hasResolvedChart = !!bmsFaultsMeta?.resolvedFaultsChart;
  const showChartsSection = hasTriggeredChart || hasResolvedChart;

  return (
    <>
      <TableContainer>
        <GenericDraggableTableView
          tableID={"BatteryBmsFaults"}
          title={"Battery System Faults"}
          count={getCountText()}
          filteredCount={getFilteredCount()}
          rowsPerPage={pageCount}
          showPrimaryButton={false}
          disableSearch={true}
          getCSVData={getCSVData}
          getCSVFileName={getCSVFileName}
          onSendEmail={onSendEmail}
          availableEmailDomains={
            pageContent?.metaInfo?.availableEmailDomains || ["coulomb.ai"]
          }
          filters={availableFilters}
          selectedFilters={selectedFilters}
          applyFilters={(args) => applySelectedFilters(args)}
          tableColumns={currentTableColumns}
          initialColumns={availableColumns}
          selectedColumns={selectedColumns}
          applySelectedColumns={applySelectedColumns}
          tableData={tableData}
          onColumnOrderChange={onColumnOrderChange}
          columnOrder={columnOrder}
          getCsvColumnSettings={getCsvColumnParams}
          isLoading={isLoading}
          pageNumber={pageNumber}
          total={pageInfo.total}
          applySort={applySort}
          onPageChange={onPageChange}
          emptyMessage={getNoDataMessage()}
          setSelectedRowKeys={setSelectedRowKeys}
          getTotalRowSelection={getTotalRowSelection}
          getTotalRowSelectionCount={getTotalRowSelectionCount}
          onClearSelection={() => {
            setTotalSelectedRowKeys(new Set());
            setIsSelectAll(false);
          }}
          rowSelection={{
            selectedRowKeys: getCurrentPageSelection(),
            onSelect: onSelectChange,
            onSelectAll: onSelectAllChange,
          }}
          emailInfo={{
            subject: `⚠️ Attention! View Alert Details for ${batteryInfo.deviceID} `,
            body: `CSV document containing data of ${batteryInfo.deviceID} attached herewith:`,
          }}
          maxColumnsAllowed={MAX_COLUMN_SELECTION_COUNT}
          defaultSelectedColumns={defaultSelectedColumns}
        />
      </TableContainer>

      {showChartsSection && (
        <Box sx={{ mt: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 3 }}>
            <ChartCalendar
              startDate={chartStartDate}
              endDate={chartEndDate}
              onDateChange={({ startDate, endDate }) => {
                setChartStartDate(startDate);
                setChartEndDate(endDate);
              }}
              disableAfterDate={analyticsCalendarEndDate()}
              forcePresets={true}
              resetTo={{ startDate: defaultChartStartDate, endDate: defaultChartEndDate }}
            />
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {hasTriggeredChart && (
              <BatteryTriggeredFaultChart
                batteryID={batteryID}
                startDate={chartStartDate}
                endDate={chartEndDate}
                onBarClick={(faultName, severityName) => handleChartBarClick("triggeredFaultBreakdown", faultName, null, severityName)}
              />
            )}
            {hasResolvedChart && (
              <BatteryResolutionDurationChart
                batteryID={batteryID}
                startDate={chartStartDate}
                endDate={chartEndDate}
                onBarClick={(faultName, resolutionType) => handleChartBarClick("resolutionDurationBreakdown", faultName, resolutionType)}
              />
            )}
          </Box>
        </Box>
      )}

      <BmsFaultDrillDownModal
        open={drilldownOpen}
        onClose={() => setDrilldownOpen(false)}
        chartName={drilldownChartName}
        faultName={drilldownFaultName}
        batteryID={batteryID}
        resolutionType={drilldownResolutionType}
        severityName={drilldownSeverityName}
        startDate={chartStartDate}
        endDate={chartEndDate}
        pageContent={pageContent}
        customRenderers={customRenderers}
      />

      <AlertActionDialog
        actionData={actionDialogData}
        open={openActionDialog}
        onClose={(hasUpdatedData) => {
          setOpenActionDialog(false);
          // Reload data only if changes were saved
          if (hasUpdatedData) {
            fetchDigitalTwinAlerts();
          }
        }}
      />
      <SingleAlertAnalysisDialog
        analyseData={analyseDialogData}
        open={openAnalyseDialog}
        onClose={() => setOpenAnalyseDialog(false)}
      />
    </>
  );
};

export default BatteryBmsFaults;
